import React, { useState, useEffect, useRef } from 'react';
import * as S from './widgets.styles';

interface HeaderTextWidgetProps {
  content: string;
  variant: 'h1' | 'h2' | 'h3' | 'h4';
  onAnimationComplete?: () => void;
  shouldStart?: boolean;
}

const HeaderTextWidget: React.FC<HeaderTextWidgetProps> = ({
  content,
  variant,
  onAnimationComplete,
  shouldStart = true,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<string>('');
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onAnimationComplete);

  // Update callback ref when it changes
  useEffect(() => {
    onCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  useEffect(() => {
    if (!content) {
      setDisplayedText('');
      setIsAnimating(false);
      contentRef.current = '';
      return;
    }

    // If shouldStart is false (e.g., during streaming), show content immediately and call completion
    if (!shouldStart) {
      setDisplayedText(content);
      setIsAnimating(false);
      contentRef.current = content;
      // Call completion immediately so suffix icons can be shown
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
      return;
    }

    const previousContent = contentRef.current;
    const isNewContent = content !== previousContent;

    if (isNewContent) {
      // Clear any existing animation
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }

      contentRef.current = content;

      // Start typing animation
      setDisplayedText('');
      setIsAnimating(true);

      let currentIndex = 0;
      const targetContent = content;
      animationIntervalRef.current = setInterval(() => {
        if (currentIndex < targetContent.length) {
          setDisplayedText(targetContent.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }
          setIsAnimating(false);
          // Call completion callback
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }
      }, 5); // 5ms per character for fast typing effect
    }

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [content, shouldStart]);

  // Render based on variant
  const renderTitle = () => {
    // Always use displayedText when animating, or content when not animating
    const text = shouldStart && isAnimating ? displayedText : shouldStart && !isAnimating ? content : content;
    const showCursor = isAnimating && displayedText.length < content.length;

    if (variant === 'h1') {
      return (
        <S.HeaderTitleH1>
          {text || '\u00A0'}
          {showCursor && <S.TypingCursor />}
        </S.HeaderTitleH1>
      );
    }

    if (variant === 'h2') {
      return (
        <S.HeaderTitleH2>
          {text || '\u00A0'}
          {showCursor && <S.TypingCursor />}
        </S.HeaderTitleH2>
      );
    }
    if (variant === 'h3') {
      return (
        <S.HeaderTitleH3>
          {text || '\u00A0'}
          {showCursor && <S.TypingCursor />}
        </S.HeaderTitleH3>
      );
    }

    if (variant === 'h4') {
      return (
        <S.HeaderTitleH4>
          {text || '\u00A0'}
          {showCursor && <S.TypingCursor />}
        </S.HeaderTitleH4>
      );
    }

    return null;
  };

  return renderTitle();
};

export default HeaderTextWidget;
